"use client";

import { useEffect } from "react";

import { SITE_COMMIT, SITE_VERSION } from "../../lib/version";
import { PWA_PHASE3 } from "./pwa/flags";
import { setRegistration } from "./pwa/updates";

// Production only: in `next dev` a service worker would cache HMR chunks and
// serve stale code between edits. Mounted in the root layout, so the worker is
// in place whichever page the app is installed from (docs/frontend.md#service-worker).
//
// Phase 3 (F173): the site-wide worker /sw.js, scope "/", registered with the
// build in its URL so every deploy installs a new one (and its caches); the
// update flow itself is pwa/updates.ts. The phases 1–2 worker (scope "/app") is
// unregistered: as the narrower scope it would keep controlling /app. The
// rollback switch (pwa/flags.ts) does the reverse.
export function ServiceWorkerRegistration() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    // Dev: no worker, and none left from a production build tried on dev (its
    // cache-first static files would serve dev's stale CSS and JS).
    if (process.env.NODE_ENV !== "production") {
      void navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((reg) => void reg.unregister()));
      return;
    }
    void (async () => {
      const root = new URL("/", window.location.origin).href;
      for (const reg of await navigator.serviceWorker.getRegistrations()) {
        if ((reg.scope === root) !== PWA_PHASE3) await reg.unregister();
      }
      if (!PWA_PHASE3) {
        // Scope "/app" (no trailing slash) so it also controls the bare /app
        // URL; sw.js is served with Service-Worker-Allowed (next.config.js).
        await navigator.serviceWorker.register("/app/sw.js", { scope: "/app" });
        return;
      }
      const reg = await navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(`${SITE_VERSION}-${SITE_COMMIT}`)}`, { scope: "/" });
      setRegistration(reg);
      // This page already runs the newest build (it registered that build's
      // worker), so the new worker takes over at once; it only swaps caches.
      // Tabs still on older code see the update card or screen.
      // The install may already be under way when register() resolves, so
      // the installing worker is watched as well as later ones.
      const activate = (worker: ServiceWorker | null) => worker?.postMessage({ type: "SKIP_WAITING" });
      const whenInstalled = (worker: ServiceWorker | null) =>
        worker?.addEventListener("statechange", () => worker.state === "installed" && activate(worker));
      activate(reg.waiting);
      whenInstalled(reg.installing);
      reg.addEventListener("updatefound", () => whenInstalled(reg.installing));
    })().catch(() => {
      /* no worker: the site works without one, updates become plain reloads */
    });
  }, []);

  return null;
}
